import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useTropicalDemoKind } from 'utils/featureFlags';
import {
  JAPAN_FACILITIES,
  JAPAN_RESPONSE_RESOURCES,
  formatJapanAgency,
  formatJapanFacilityLabel,
  formatJapanResponseResourceName,
  isJapanResourceSet,
  isJapanResponseResourceSet,
  japanChecklistsForAutomationRules,
  japanChecklistsForThreats,
  japanFacilityIdFromResourceNotes,
  japanResourceNotesMarker,
  japanResponseResourceNotesMarker,
  type JapanFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/japanEarthquakeNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const JAPAN_FACILITY_KIND = 'Facilities';
const JAPAN_FACILITY_TYPE = 'Defense Critical Infrastructure';
const JAPAN_RESPONSE_SYMBOLOGY = 'stagingArea';

function checklistItemsMatch(
  actual: OrgEventChecklistItemDef[] | undefined,
  expected: OrgEventChecklistItemDef[]
): boolean {
  if (!Array.isArray(actual) || actual.length !== expected.length) {
    return false;
  }
  return expected.every((item, index) => {
    const current = actual[index];
    return (
      current?.id === item.id &&
      current?.kind === item.kind &&
      current?.label === item.label
    );
  });
}

function seedMatchesResource(
  resource: ResourceSet,
  seed: JapanFacility
): boolean {
  if (
    resource.name !== formatJapanFacilityLabel(seed) ||
    resource.symbology !== seed.symbology
  ) {
    return false;
  }
  const threats = Array.isArray(resource.threats) ? resource.threats : [];
  if (
    threats.length !== seed.threats.length ||
    !seed.threats.every((threat) => threats.includes(threat))
  ) {
    return false;
  }
  const expectedThreatChecklists = japanChecklistsForThreats(seed.threats);
  const expectedRuleChecklists = japanChecklistsForAutomationRules(
    seed.threats
  );
  const ruleNames = expectedRuleChecklists.automationRules;
  const storedRules = Array.isArray(resource.automation_rules)
    ? resource.automation_rules
    : [];
  if (
    storedRules.length !== ruleNames.length ||
    !ruleNames.every((name) => storedRules.includes(name))
  ) {
    return false;
  }
  const threatsMatch = seed.threats.every((threat) =>
    checklistItemsMatch(
      resource.threat_checklists?.[threat],
      expectedThreatChecklists[threat] ?? []
    )
  );
  const rulesMatch = ruleNames.every((name) =>
    checklistItemsMatch(
      resource.automation_rule_checklists?.[name],
      expectedRuleChecklists.automationRuleChecklists[name] ?? []
    )
  );
  return threatsMatch && rulesMatch;
}

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchStaleJapanSeeds({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const seedsById = new Map(JAPAN_FACILITIES.map((seed) => [seed.id, seed]));
  const stale = resources.flatMap((resource) => {
    if (!isJapanResourceSet(resource)) return [];
    const seed = seedsById.get(
      japanFacilityIdFromResourceNotes(resource.notes) ?? ''
    );
    if (!seed || seedMatchesResource(resource, seed)) return [];
    return [{ resource, seed }];
  });
  if (stale.length === 0) return false;

  await Promise.all(
    stale.map(({ resource, seed }) => {
      const ruleSeed = japanChecklistsForAutomationRules(seed.threats);
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        name: formatJapanFacilityLabel(seed),
        symbology: seed.symbology,
        threats: seed.threats,
        threatChecklists: {
          ...(resource.threat_checklists ?? {}),
          ...japanChecklistsForThreats(seed.threats),
        },
        automationRules: ruleSeed.automationRules,
        automationRuleChecklists: {
          ...(resource.automation_rule_checklists ?? {}),
          ...ruleSeed.automationRuleChecklists,
        },
      };
      const {
        members: _members,
        threatChecklists: checklists,
        automationRuleChecklists: ruleChecklists,
        ...dataForBackend
      } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          threat_checklists: checklists,
          automation_rule_checklists: ruleChecklists,
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

function facilityResourceBody(
  facility: JapanFacility,
  groupGuid: string,
  userGuid: string
) {
  const label = formatJapanFacilityLabel(facility);
  const ruleSeed = japanChecklistsForAutomationRules(facility.threats);
  return {
    name: label,
    description: facility.inImpactArea
      ? `${label} — inside the Tokyo Bay earthquake impact area.`
      : `${label} — supporting the HADR and NEO response.`,
    kind: JAPAN_FACILITY_KIND,
    type: JAPAN_FACILITY_TYPE,
    quantity: 1,
    status: 'In Organization',
    asset_status: 'PMC',
    asset_status_timestamp: new Date().toISOString(),
    address: `${facility.city}, ${facility.state}`,
    symbology: facility.symbology,
    threats: facility.threats,
    threat_checklists: japanChecklistsForThreats(facility.threats),
    automation_rules: ruleSeed.automationRules,
    automation_rule_checklists: ruleSeed.automationRuleChecklists,
    notes: japanResourceNotesMarker(facility.id),
    geometry: {
      type: 'Point',
      coordinates: [facility.lon, facility.lat],
    },
    incidents: [],
    archived: false,
    group_guid: groupGuid,
    user_guid: userGuid,
    incident_id: null,
  };
}

function responseResourceBody(
  resource: (typeof JAPAN_RESPONSE_RESOURCES)[number],
  groupGuid: string,
  userGuid: string
) {
  return {
    name: formatJapanResponseResourceName(resource),
    description: `${resource.quantity} ${resource.unit} staged at ${resource.stagedAt}. Owner: ${formatJapanAgency(resource.agency)}.`,
    kind: resource.kind,
    type: resource.type,
    quantity: resource.quantity,
    status: 'In Organization',
    asset_status: 'FMC',
    asset_status_timestamp: new Date().toISOString(),
    address: resource.stagedAt,
    symbology: JAPAN_RESPONSE_SYMBOLOGY,
    threats: resource.threats,
    threat_checklists: {},
    automation_rules: [],
    automation_rule_checklists: {},
    notes: japanResponseResourceNotesMarker(resource.id),
    geometry: {
      type: 'Point',
      coordinates: [resource.lon, resource.lat],
    },
    incidents: [],
    archived: false,
    group_guid: groupGuid,
    user_guid: userGuid,
    incident_id: null,
  };
}

async function ensureJapanResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(
        (resource) =>
          isJapanResourceSet(resource) || isJapanResponseResourceSet(resource)
      )
      .map((resource) => resource.notes as string)
  );

  const facilitiesToCreate = JAPAN_FACILITIES.filter(
    (facility) => !existingMarkers.has(japanResourceNotesMarker(facility.id))
  );
  const responseToCreate = JAPAN_RESPONSE_RESOURCES.filter(
    (resource) =>
      !existingMarkers.has(japanResponseResourceNotesMarker(resource.id))
  );

  let didMutate = false;

  if (facilitiesToCreate.length > 0 || responseToCreate.length > 0) {
    await Promise.all([
      ...facilitiesToCreate.map((facility) =>
        axios.post(
          config.apiGateway.ics_resource_sets,
          facilityResourceBody(facility, groupGuid, userGuid)
        )
      ),
      ...responseToCreate.map((resource) =>
        axios.post(
          config.apiGateway.ics_resource_sets,
          responseResourceBody(resource, groupGuid, userGuid)
        )
      ),
    ]);
    didMutate = true;
  }

  const patched = await patchStaleJapanSeeds({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

/**
 * Seeds one resource set per Tokyo Bay facility, plus the named HADR and NEO
 * response resources with their quantities and owning agency. Notes markers are
 * the idempotency handle, so reloads never duplicate them.
 */
export function useEnsureJapanResourceSets() {
  const japanEnabled = useTropicalDemoKind() === 'japan';
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!japanEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureJapanResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[Japan] Failed to ensure Tokyo Bay resource sets',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [japanEnabled, groupGuid, userGuid, queryClient]);
}
